import toPairs from "lodash/toPairs";
import { produce } from "immer";

import { definedMap, withDefault } from "collection-utils";

import {
    AppDescription,
    AppKind,
    defaultUserFeatures,
    getAppTabs,
    getSourceMetadata,
    SourceMetadata,
    getAppKind,
} from "../components/SerializedApp";
import {
    chatScreen,
    makeScreenNameWithPrefix,
    copyToFreeScreen,
    shoppingCartScreen,
    addOrRemoveSpecialTabsIfNecessary,
} from "./screens";
import { makeAddOrEditScreenDescriptionForTable, makeArrayScreenDescription } from "./make-screen";
import {
    isFormScreenName,
    ScreenDescriptionKind,
    isAddOrEditOrFormScreenDescription,
    isVariantScreenName,
    isFreeScreen,
    getClassScreenTableName,
    isFreeScreenName,
    getArrayScreenTableName,
    TypeSchema,
    chatScreenName,
    isEditClassScreenName,
    addClassScreenName,
    makeTableRef,
    classScreenName,
    editClassScreenName,
    TabDescription,
    freeScreenPrefix,
    ScreenDescription,
    findTable,
    makeTableName,
    arrayScreenName,
    EditScreenDescription,
    ComponentDescription,
    ArrayScreenFormat,
    ArrayContentDescription,
    ArrayTransformKind,
    TransformableContentDescription,
    getArrayTransforms,
    PropertyKind,
    FilterArrayTransform,
    UnaryPredicateCompositeOperator,
    ArrayTransform,
    Formula,
    ArrayFilter,
    getClassOrArrayScreenTableName,
    getTableName,
    shoppingCartScreenName,
    ClassOrArrayScreenDescription,
    ActionDescription,
    userProfileScreenName,
    makeSourceColumn,
    findFilterArrayTransform,
    FormulaKind,
    isComputedColumn,
    ActionKind,
    PropertyDescription,
    getDefaultContextColumnName,
    FormScreenDescription,
    makeTypeSchema,
    getScreenComponentsArray,
    ContainerComponentDescription,
    areTableNamesEqual,
    isAddClassScreenName,
} from "./description";
import {
    getScreenProperty,
    getEnumProperty,
    makeEnumProperty,
    getSwitchProperty,
    makeScreenProperty,
    getSourceColumnProperty,
} from "./components/property-utils";
import { logError } from "../debug-print";
import { ActionsRecord, makeSimpleComponentConfiguratorContext, mapActionsRecord } from "./components";
import { panic, assert, defined, mapFilterUndefined, truthify, isArray } from "../Support";
import { isInlineListComponentDescription, InlineListComponentDescription } from "./components/inline-list";
import { getColumnAssignmentsWithLegacy, getEmailArrayFilter, makeEmailArrayFilter } from "./description-utils";
import {
    UnaryPredicateSpecification,
    PredicateCombinationSpecification,
    PredicateSpecification,
    decomposePredicateCombinationFormula,
    makePredicateCombinationFormula,
    decomposeFormatFormula,
    decomposeAll,
} from "./formulas/formula-specifications";
import { handlerForArrayScreenFormat, getDefaultArrayScreenHandler } from "./array-screens";
import { ComponentKindBuyButton } from "./components/buy-button";
import { handlerForComponentKind } from "./handlers";
import { handlerForActionKind } from "./actions";
import { EminenceFlags } from "../Database/eminence";
import { PushFreeScreenActionDescription } from "./actions/link-to-screen";
import { BuilderActionsForApp } from "../Database/builder-actions";
import { makeShowToastAction, ToastIcon } from "./form-on-submit";
import { forEachActionInApp } from "./actions/for-each-action";
import { DefaultMap, hasOwnProperty, Writable } from "@glideapps/ts-necessities";
import { getNativeTableIDFromTableName } from "../database-strings";
import { getSourceMetadataForTable } from "./schema-utils";
import { defaultRowIDColumnName } from "../google-sheets";
import { makeRowID } from "../make-row-id";
import { makeDefaultClassScreen } from "./pages";

function isPrimitiveScreenName(name: string): boolean {
    return name.startsWith("primitive-");
}

function doesLinkToScreenFetchData(source: PropertyDescription | undefined): boolean {
    return getSourceColumnProperty(source) === undefined && getEnumProperty(source) === undefined;
}

// Returns a list of issues, or `undefined` if everything's ok.
// If `schema` is defined, we do more strict checks.
export function checkAppDescriptionConsistency(
    app: AppDescription,
    schema: TypeSchema | undefined,
    isSaved: boolean
): readonly string[] | undefined {
    const strict = schema !== undefined;
    const issues: string[] = [];

    function checkArrayTransforms(transforms: readonly ArrayTransform[], where: string): void {
        for (const t of transforms) {
            if (t.kind === ArrayTransformKind.Filter) {
                const decomposed = decomposePredicateCombinationFormula(t.predicate)?.spec;
                if (decomposed === undefined) {
                    issues.push(`Cannot decompose filter for ${where}`);
                }
            } else if (t.kind === ArrayTransformKind.Sort) {
                for (const k of t.keys) {
                    if (k.key.kind !== FormulaKind.GetColumn) {
                        issues.push(`Sort key is invalid in ${where}`);
                    }
                }
            }
        }
    }

    function checkAction(action: ActionDescription, fromScreenName: string): void {
        if (action.kind !== ActionKind.PushFreeScreen) return;

        const pushAction = action as PushFreeScreenActionDescription;
        const pushedScreenName = pushAction.screenName;
        if (pushedScreenName === fromScreenName) {
            issues.push(`Link to screen links to the same screen ${fromScreenName}`);
            return;
        }

        const screen = app.screenDescriptions[pushedScreenName];
        if (screen === undefined) {
            issues.push(`Missing screen ${pushedScreenName} for Show New Screen in ${fromScreenName}`);
            return;
        }
        if (screen.kind !== ScreenDescriptionKind.Class && screen.kind !== ScreenDescriptionKind.Array) {
            issues.push(`Link to screen ${pushedScreenName} is not class or array screen`);
            return;
        }

        if (isSaved) {
            const fetchesData = doesLinkToScreenFetchData(pushAction.sourceColumn);
            if (truthify(screen.fetchesData) !== fetchesData) {
                issues.push(`Link to screen ${pushedScreenName} does not correctly fetch data`);
                return;
            }
        }
    }

    const counts = getTableNameCounts(getTabScreenCounts(app));
    for (const [n, c] of Array.from(counts)) {
        if (c > 1) {
            issues.push(`More than one tab with table ${n}`);
        }
    }
    for (const screenName of Object.keys(app.screenDescriptions)) {
        if (screenName === userProfileScreenName) continue;

        if (strict && isPrimitiveScreenName(screenName)) {
            issues.push(`Primitive screen still exists ${screenName}`);
        }

        const screen = app.screenDescriptions[screenName];
        const tableName = getClassOrArrayScreenTableName(screenName);

        if (isFormScreenName(screenName)) {
            if (screen.kind !== ScreenDescriptionKind.Class) {
                issues.push(`Form screen is not a class screen ${screenName}`);
            } else {
                const formScreen = screen as FormScreenDescription;
                if (screen.isForm !== true) {
                    issues.push(`Form screen doesn't have form flag set ${screenName}`);
                }
                if (screen.title !== undefined) {
                    issues.push(`Form screen has a title ${screenName}`);
                }
                if (formScreen.onSubmitActions !== undefined && !isArray(formScreen.onSubmitActions)) {
                    issues.push(`Form screen has no actions ${screenName}`);
                }
            }
        } else {
            if (screen.kind === ScreenDescriptionKind.Class) {
                if (screen.isForm === true) {
                    issues.push(`Non-form class screen has form flag set ${screenName}`);
                }
                if (isFreeScreen(screenName, screen)) {
                    if (screen.title !== undefined) {
                        issues.push(`Free screen has a title ${screenName}`);
                    }
                }
            }
        }

        if (isAddClassScreenName(screenName) || isEditClassScreenName(screenName)) {
            if (screen.kind !== ScreenDescriptionKind.Class) {
                issues.push(`Add or edit screen is not a class screen ${screenName}`);
            }
        }

        if (screen.kind === ScreenDescriptionKind.Array || screen.kind === ScreenDescriptionKind.Class) {
            if (isAddOrEditOrFormScreenDescription(screenName, screen)) {
                if (strict) {
                    if (screen.columns !== undefined) {
                        issues.push(`Screen still has columns ${screenName}`);
                    }
                }
            } else {
                if (isVariantScreenName(screenName)) {
                    // We don't care about variant screens
                } else if (screen.kind === ScreenDescriptionKind.Array && screen.type.kind !== "table-ref") {
                    // We don't care about primitive screens
                } else if (isFreeScreen(screenName, screen) && tableName !== undefined) {
                    issues.push(`Regular class/array screen fetches data ${screenName}`);
                }
            }
        }

        if (getClassScreenTableName(screenName) !== undefined) {
            if (screen.kind !== ScreenDescriptionKind.Class) {
                issues.push(`Screen named as class screen is not a class screen ${screenName}`);
                continue;
            }
            if (isFreeScreen(screenName, screen)) {
                issues.push(`Class screen cannot fetch data ${screenName}`);
            }
        }

        if (getArrayScreenTableName(screenName) !== undefined) {
            if (screen.kind !== ScreenDescriptionKind.Array) {
                issues.push(`Screen named as array screen is not an array screen ${screenName}`);
                continue;
            }
            if (isFreeScreen(screenName, screen)) {
                issues.push(`Array screen cannot fetch data ${screenName}`);
            }
        }

        if (screen.kind === ScreenDescriptionKind.Class || screen.kind === ScreenDescriptionKind.Array) {
            if (strict) {
                if (screen.filter !== undefined) {
                    issues.push(`Array screen still has filter set ${screenName}`);
                }
            }
            for (const c of getScreenComponentsArray(screen)) {
                if (c.kind === undefined) {
                    issues.push(`Screen has components without kind ${screenName}`);
                    break;
                }
                // if (c.spanCoordsFloat === undefined) {
                //     issues.push(`Screen has no span coords ${screenName}`);
                //     break;
                // }
                if (isInlineListComponentDescription(c)) {
                    if (strict) {
                        if (c.reverse !== undefined) {
                            issues.push(`Inline list still has reverse set in screen ${screenName}`);
                        }
                        if (c.filter !== undefined) {
                            issues.push(`Inline list still has filter set in screen ${screenName}`);
                        }
                    }

                    checkArrayTransforms(getArrayTransforms(c), `inline list in screen ${screenName}`);
                }
            }
        }
        if (screen.kind === ScreenDescriptionKind.Class) {
            if (screen.components === undefined) {
                issues.push(`Class screen has no components array ${screenName}`);
            }
        }
        if (screen.kind === ScreenDescriptionKind.Array) {
            if (strict) {
                if (screen.reverse !== undefined) {
                    issues.push(`Array screen still has reverse set ${screenName}`);
                }
            }

            checkArrayTransforms(getArrayTransforms(screen), `screen ${screenName}`);
        }
    }

    forEachActionInApp(app, checkAction);

    for (const tab of withDefault(app.tabs, [])) {
        const screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) {
            issues.push(`No screen name for tab`);
            continue;
        }
        const screen = app.screenDescriptions[screenName];
        if (screen === undefined) {
            issues.push(`No screen for tab ${screenName}`);
            continue;
        }
        if (tab.propertyName !== undefined) {
            issues.push(`Tab has a property set for screen ${screenName}`);
        }

        if (screen.kind === ScreenDescriptionKind.Array || screen.kind === ScreenDescriptionKind.Class) {
            if (!isFreeScreen(screenName, screen)) {
                issues.push(`Tab screen is not free screen ${screenName}`);
            } else if (screen.fetchesData !== true) {
                issues.push(`Tab screen does not fetch data ${screenName}`);
            }
        }

        checkArrayTransforms(tab.visibilityFilters ?? [], `tab ${tab.screenName}`);
    }

    if (schema !== undefined) {
        for (const table of schema.tables) {
            const tableName = getTableName(table);
            if (tableName.isSpecial) continue;

            const tableRef = makeTableRef(table);
            if (app.screenDescriptions[arrayScreenName(tableRef)] === undefined) {
                issues.push(`Array screen missing ${tableName.name}`);
            }
            if (app.screenDescriptions[classScreenName(tableRef)] === undefined) {
                issues.push(`Class screen missing ${tableName.name}`);
            }
            if (app.screenDescriptions[editClassScreenName(tableRef)] === undefined) {
                issues.push(`Edit class screen missing ${tableName.name}`);
            }
            if (app.screenDescriptions[addClassScreenName(tableRef)] === undefined) {
                issues.push(`Add class screen missing ${tableName.name}`);
            }
        }
    }

    if (issues.length === 0) return undefined;
    return issues;
}

export function checkSchemaFormulas(schema: TypeSchema): readonly string[] | undefined {
    const issues: string[] = [];

    for (const table of schema.tables) {
        const tableName = getTableName(table);
        for (const column of table.columns) {
            if (!isComputedColumn(column)) continue;

            const columnSpec = decomposeAll(column.formula);
            if (columnSpec === undefined) {
                issues.push(
                    `Cannot decompose computed column formula in table ${tableName.name} column ${column.name}`
                );
            }

            if (column.displayFormula !== undefined) {
                const formatSpec = decomposeFormatFormula(column.displayFormula);
                if (formatSpec === undefined) {
                    issues.push(
                        `Cannot decompose computed column display formula in table ${tableName.name} column ${column.name}`
                    );
                }
            }
        }
    }

    if (issues.length === 0) return undefined;
    return issues;
}

function replaceFilterArrayTransform(
    transforms: readonly ArrayTransform[],
    predicate: Formula
): readonly ArrayTransform[] {
    return [
        ...transforms.filter(t => t.kind !== ArrayTransformKind.Filter),
        { kind: ArrayTransformKind.Filter, predicate, isActive: true },
    ];
}

function isEmailFilter(specs: readonly PredicateSpecification[]): string | undefined {
    if (specs.length !== 2) return undefined;

    if (
        specs.length === 2 &&
        specs[0].operator === UnaryPredicateCompositeOperator.MatchesVerifiedEmailAddress &&
        specs[1].operator === UnaryPredicateCompositeOperator.IsEmpty &&
        specs[0].column.kind === specs[1].column.kind &&
        specs[0].column.name === specs[1].column.name
    ) {
        return getDefaultContextColumnName(specs[0].column);
    }

    return undefined;
}

export function undoFaultyEmailTransform(
    transforms: readonly ArrayTransform[],
    how: "soft" | "hard"
): { filter: ArrayFilter; transforms: readonly ArrayTransform[] } | false | undefined {
    const filterTransform = findFilterArrayTransform(transforms);
    if (filterTransform === undefined) return;

    const maybeSpec = decomposePredicateCombinationFormula(filterTransform.predicate)?.spec;
    if (maybeSpec === undefined) {
        return panic(`Could not decompose filter predicate`);
    }

    const { predicates } = maybeSpec;
    const emailFilterPredicate = predicates.find(
        s => s.operator === UnaryPredicateCompositeOperator.MatchesVerifiedEmailAddress
    );

    const emailFilterColumn = definedMap(emailFilterPredicate?.column, getDefaultContextColumnName);
    if (emailFilterColumn === undefined) return undefined;

    if (how === "hard") {
        const newSpec = {
            ...maybeSpec,
            combinator: "and" as const,
            predicates: predicates.filter(
                p => p.operator !== UnaryPredicateCompositeOperator.MatchesVerifiedEmailAddress
            ),
        };
        const predicate = makePredicateCombinationFormula(newSpec);
        return {
            filter: makeEmailArrayFilter(emailFilterColumn),
            transforms: replaceFilterArrayTransform(transforms, predicate),
        };
    }

    if (maybeSpec.combinator === "and" || predicates.length < 2) return undefined;

    const filterColumn = isEmailFilter(predicates.slice(predicates.length - 2));
    if (filterColumn === undefined) return false;

    if (predicates.length === 2) return undefined;

    const newSpec = { ...maybeSpec, predicates: predicates.slice(0, predicates.length - 2) };
    const predicate = makePredicateCombinationFormula(newSpec);

    const newTransforms = replaceFilterArrayTransform(transforms, predicate);
    return { filter: makeEmailArrayFilter(filterColumn), transforms: newTransforms };
}

function convertTransforms<T extends TransformableContentDescription>(
    desc: T,
    undoFaultyEmail: false | "soft" | "hard",
    log: (...args: readonly any[]) => void
): T {
    if (undoFaultyEmail !== false) {
        const result = undoFaultyEmailTransform(getArrayTransforms(desc), undoFaultyEmail);
        if (result === undefined || result === false) return desc;

        return { ...desc, filter: result.filter, transforms: result.transforms };
    }

    const filter = getEmailArrayFilter(desc);
    if (filter === undefined) return desc;

    const { value } = filter;
    if (value.kind !== PropertyKind.Column || typeof value.value !== "string") {
        log("Email filter value is not a column, but is", value.kind);
        return { ...desc, filter: undefined };
    }
    const column = value.value;

    let filterTransforms = getArrayTransforms(desc).filter(
        t => t.kind === ArrayTransformKind.Filter
    ) as FilterArrayTransform[];
    if (filterTransforms.length > 1) {
        return panic(`More than one array filter`);
    }

    if (filterTransforms.length === 1 && filterTransforms[0].isActive === false) {
        filterTransforms = [];
    }

    let predicates: UnaryPredicateSpecification[] = [
        {
            kind: "unary",
            column: makeSourceColumn(column),
            operator: UnaryPredicateCompositeOperator.MatchesVerifiedEmailAddress,
        },
        {
            kind: "unary",
            column: makeSourceColumn(column),
            operator: UnaryPredicateCompositeOperator.IsEmpty,
        },
    ];

    let spec: PredicateCombinationSpecification;
    if (filterTransforms.length === 1) {
        const maybeSpec = decomposePredicateCombinationFormula(filterTransforms[0].predicate)?.spec;
        if (maybeSpec === undefined) {
            logError(`Could not decompose filter predicate`);
            return desc;
        }
        if (maybeSpec.combinator !== "and" && maybeSpec.predicates.length > 1) {
            logError("We have more than one predicates that are ored");

            // In this case we cannot represent the filter condition, so we leave
            // the existing email filter as-is.
            return desc;
        }

        // We can't add the or of both our conditions, so we just use the first
        // one, which is conservative.
        predicates = predicates.slice(0, 1);
        spec = { ...maybeSpec, combinator: "and" };
    } else {
        spec = { kind: "predicate-combination", combinator: "or", predicates: [] };
    }

    spec = { ...spec, predicates: [...spec.predicates, ...predicates] };
    const predicate = makePredicateCombinationFormula(spec);

    const transforms = replaceFilterArrayTransform(getArrayTransforms(desc), predicate);
    return { ...desc, filter: undefined, transforms };
}

function fixActions(actionsRecord: ActionsRecord): ActionsRecord | undefined {
    let didChange = false;
    const newActionsRecord = mapActionsRecord(actionsRecord, actions => {
        const newActions: ActionDescription[] = [];
        for (const action of actions) {
            const actionHandler = handlerForActionKind(action.kind);
            const newAction = actionHandler?.fixActionDescription(action);
            if (newAction !== undefined) {
                newActions.push(newAction);
                didChange = true;
            } else {
                newActions.push(action);
            }
        }
        return newActions;
    });

    if (!didChange) return undefined;
    return newActionsRecord;
}

function convertArrayContent(
    format: ArrayScreenFormat,
    content: ArrayContentDescription,
    undoFaultyEmail: false | "soft" | "hard",
    log: (...args: readonly any[]) => void
): { properties: ArrayContentDescription; format: ArrayScreenFormat } | undefined {
    let didChange = false;

    const fixedTransforms = convertTransforms(content, undoFaultyEmail, log);
    if (fixedTransforms !== content) {
        content = fixedTransforms;
        didChange = true;
    }

    if (withDefault(content.transforms, []).length === 0 && truthify(getSwitchProperty(content.reverse))) {
        content = { ...content, transforms: [{ kind: ArrayTransformKind.TableOrder, reverse: true }] };
        didChange = true;
    }
    if (content.reverse !== undefined) {
        content = { ...content, reverse: undefined };
        didChange = true;
    }

    const handler = handlerForArrayScreenFormat(format);
    const actions = definedMap(handler, h => fixActions(h.getActions(content, undefined)));
    const fixedContent = handler?.fixContentDescription(content);
    if (fixedContent !== undefined) {
        if (actions === undefined) {
            return fixedContent;
        } else {
            return { properties: { ...fixedContent.properties, ...actions }, format: fixedContent.format };
        }
    } else {
        if (didChange) {
            if (actions !== undefined) {
                content = { ...content, ...actions };
            }
            return { properties: content, format };
        }
        return undefined;
    }
}

function convertComponent(
    desc: ComponentDescription,
    undoFaultyEmail: false | "soft" | "hard",
    log: (...args: readonly any[]) => void
): ComponentDescription | undefined {
    function withActions(d: ComponentDescription | undefined): ComponentDescription | undefined {
        let actions: ActionsRecord | undefined;

        const componentHandler = handlerForComponentKind((d ?? desc).kind);
        if (componentHandler !== undefined) {
            actions = fixActions(componentHandler.getActions(d ?? desc, undefined, undefined, undefined));
        }

        if (actions === undefined) return d;
        return { ...(d ?? desc), ...actions };
    }

    if (!isInlineListComponentDescription(desc)) return withActions(undefined);

    const format = getEnumProperty<ArrayScreenFormat>(desc.format);
    if (format === undefined) return withActions(undefined);

    const converted = convertArrayContent(format, desc, undoFaultyEmail, log);
    if (converted === undefined) return withActions(undefined);

    const fixed: InlineListComponentDescription = {
        ...desc,
        ...converted.properties,
        format: makeEnumProperty(converted.format),
    };

    return withActions(fixed);
}

function fixColumnAssignments(screen: EditScreenDescription): EditScreenDescription {
    if (screen.columns === undefined) return screen;
    const columnAssignments = getColumnAssignmentsWithLegacy(screen);
    return { ...screen, columns: undefined, columnAssignments };
}

function getTabScreenCounts(appDescription: AppDescription): DefaultMap<string, number> {
    const screenCounts = new DefaultMap<string, number>(() => 0);
    for (const tab of getAppTabs(appDescription)) {
        const screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) continue;

        screenCounts.update(screenName, n => n + 1);
    }
    return screenCounts;
}

function getTableNameCounts(tabScreenCounts: Map<string, number>): DefaultMap<string, number> {
    const nameCounts = new DefaultMap<string, number>(() => 0);
    for (const [screenName, count] of tabScreenCounts) {
        const tableName = getClassOrArrayScreenTableName(screenName);
        if (tableName === undefined) continue;

        nameCounts.update(tableName, n => n + count);
    }
    return nameCounts;
}

// FIXME: At some point we need to run this over all existing apps and then clear it out.
// We can't have unfixed apps in perpetuity, and carry all this code around.
export function fixAppDescription(
    appID: string,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema,
    eminenceFlags: EminenceFlags,
    convertFeatures: boolean = true,
    doLog: boolean = true,
    undoFaultyEmail: false | "soft" | "hard" = false
): AppDescription {
    function log(...args: readonly any[]): void {
        if (!doLog) return;
        logError(...args);
    }

    const ccc = makeSimpleComponentConfiguratorContext(
        appID,
        appDescription,
        builderActions,
        schema,
        defaultUserFeatures,
        eminenceFlags
    );
    const isPage = getAppKind(appDescription) === AppKind.Page;

    const screenDescriptions = { ...appDescription.screenDescriptions };

    function makeFreeCopy(screenName: string, screen: ClassOrArrayScreenDescription): string {
        const copyName = makeScreenNameWithPrefix(freeScreenPrefix, n => screenDescriptions[n] !== undefined);
        screenDescriptions[copyName] = copyToFreeScreen(screenName, screen);
        return copyName;
    }

    if (isPage) {
        if (screenDescriptions[chatScreenName] !== undefined) {
            delete screenDescriptions[chatScreenName];
        }
        if (screenDescriptions[shoppingCartScreenName] !== undefined) {
            delete screenDescriptions[shoppingCartScreenName];
        }
    } else {
        if (screenDescriptions[chatScreenName] === undefined) {
            screenDescriptions[chatScreenName] = chatScreen;
        }
        if (screenDescriptions[shoppingCartScreenName] === undefined) {
            screenDescriptions[shoppingCartScreenName] = shoppingCartScreen;
        }
    }

    // FIXME: Do the same for the shopping cart.

    const screenNameReassignments = new Map<string, string>();

    for (let [name, desc] of toPairs(screenDescriptions)) {
        if (name === userProfileScreenName) continue;

        if (isPrimitiveScreenName(name)) {
            delete screenDescriptions[name];
            continue;
        }

        let fixed = desc;

        if (isAddClassScreenName(name) || isEditClassScreenName(name) || isFormScreenName(name)) {
            if (fixed.kind !== ScreenDescriptionKind.Class) {
                fixed = { ...fixed, kind: ScreenDescriptionKind.Class } as ScreenDescription;
            }
        }

        if (isFormScreenName(name)) {
            assert(fixed.kind === ScreenDescriptionKind.Class);

            assert(fixed.isForm === true);
            // if (fixed.isForm !== true) {
            //     fixed = { ...fixed, isForm: true };
            // }

            if (fixed.title !== undefined) {
                fixed = { ...fixed, title: undefined };
            }
        }

        function reassignToFree(screenDesc: ClassOrArrayScreenDescription): void {
            const freeName = makeFreeCopy(name, screenDesc);
            screenNameReassignments.set(name, freeName);
            fixed = desc = screenDescriptions[freeName];
            delete screenDescriptions[name];
            name = freeName;
        }

        function fixComponents(components: readonly ComponentDescription[]): ComponentDescription[] | undefined {
            let changed = false;
            // let maxSpan = 0;
            const fixedComponents = mapFilterUndefined(components, c => {
                if (c.componentID === undefined) {
                    c = { ...c, componentID: makeRowID() };
                    changed = true;
                }

                if (hasOwnProperty(c, "components") && isArray(c.components)) {
                    let cc = c as ContainerComponentDescription;
                    cc = { ...cc, components: fixComponents(defined(cc.components)) ?? cc.components };
                    c = cc;
                    changed = true;
                }

                // if (c.spanCoordsFloat !== undefined) {
                //     maxSpan = Math.max(maxSpan, c.spanCoordsFloat[0]);
                // }
                if (c.kind === ComponentKindBuyButton && !eminenceFlags.buyButton) {
                    changed = true;
                    return undefined;
                }

                if (!convertFeatures) return c;
                const f = convertComponent(c, undoFaultyEmail, log);
                if (f === undefined) return c;
                changed = true;
                return f;
            });
            // fixedComponents = fixedComponents.map(c => {
            //     if (c.spanCoordsFloat === undefined) {
            //         maxSpan += 128;
            //         c = { ...c, spanCoordsFloat: [maxSpan, 0, 0]};
            //     }
            //     return c;
            // });
            if (!changed) return undefined;
            return fixedComponents;
        }

        if (fixed.kind === ScreenDescriptionKind.Class) {
            if (getArrayScreenTableName(name) !== undefined && fixed.fetchesData === true) {
                reassignToFree(fixed);
            }

            if (getClassScreenTableName(name) !== undefined && fixed.fetchesData === true) {
                fixed = { ...fixed, fetchesData: false };
            } else if (isFreeScreenName(name)) {
                if (fixed.title !== undefined) {
                    fixed = { ...fixed, title: undefined };
                }
            }

            if (!isArray(fixed.components)) {
                fixed = { ...fixed, components: [] };
            }

            if (fixed.components.some(c => c.kind === undefined)) {
                fixed = { ...fixed, components: fixed.components.filter(c => c.kind !== undefined) };
            }

            if (convertFeatures) {
                fixed = convertTransforms(fixed, undoFaultyEmail, log);
            }
        } else if (fixed.kind === ScreenDescriptionKind.Array) {
            if (getClassScreenTableName(name) !== undefined && fixed.fetchesData === true) {
                reassignToFree(fixed);
            }

            if (getArrayScreenTableName(name) !== undefined && fixed.fetchesData === true) {
                fixed = { ...fixed, fetchesData: false };
            }

            if (convertFeatures) {
                const converted = convertArrayContent(fixed.format, fixed, undoFaultyEmail, log);
                if (converted !== undefined) {
                    fixed = { ...fixed, ...converted.properties, format: converted.format };
                }
            }
        }

        if (fixed.kind === ScreenDescriptionKind.Class || fixed.kind === ScreenDescriptionKind.Array) {
            const components = fixComponents(getScreenComponentsArray(fixed));
            if (components !== undefined) {
                fixed = { ...fixed, components };
            }
        }

        if (isAddOrEditOrFormScreenDescription(name, fixed)) {
            if (convertFeatures) {
                fixed = fixColumnAssignments(fixed);
            }

            if (isEditClassScreenName(name)) {
                if (fixed.kind !== ScreenDescriptionKind.Class) {
                    return panic("Edit screen is not class screen");
                }

                const addName = addClassScreenName(fixed.type);
                const addDesc = screenDescriptions[addName];
                // We have a bug where somehow an add screen can become an array screen.
                // I assume there's some weird state the builder can get into where it
                // allows that.  We just wipe that screen and replace it with a copy of
                // the edit screen.
                if (addDesc === undefined || addDesc.kind !== ScreenDescriptionKind.Class) {
                    screenDescriptions[addName] = fixed;
                }
            }
        }

        if (fixed !== desc) {
            screenDescriptions[name] = fixed;
        }
    }

    for (const table of schema.tables) {
        if (getTableName(table).isSpecial) continue;

        const tableRef = makeTableRef(table);

        const screenName = classScreenName(tableRef);
        let classDesc = screenDescriptions[screenName];
        if (classDesc === undefined || classDesc.kind !== ScreenDescriptionKind.Class) {
            classDesc = makeDefaultClassScreen(table, ccc, false);
            screenDescriptions[screenName] = classDesc;
        }

        const arrayName = arrayScreenName(tableRef);
        let arrayDesc = screenDescriptions[arrayName];
        if (arrayDesc === undefined || arrayDesc.kind !== ScreenDescriptionKind.Array) {
            arrayDesc = makeArrayScreenDescription(getDefaultArrayScreenHandler(), tableRef, ccc, false);
            screenDescriptions[arrayName] = arrayDesc;
        }

        const editName = editClassScreenName(tableRef);
        let editDesc = screenDescriptions[editName];
        if (editDesc === undefined || editDesc.kind !== ScreenDescriptionKind.Class) {
            editDesc = makeAddOrEditScreenDescriptionForTable(table, true);
            screenDescriptions[editName] = editDesc;
        }

        const addName = addClassScreenName(tableRef);
        const addDesc = screenDescriptions[addName];
        if (addDesc === undefined || addDesc.kind !== ScreenDescriptionKind.Class) {
            screenDescriptions[addName] = editDesc;
        }
    }

    const screenCounts = getTabScreenCounts(appDescription);
    const tableNameCounts = getTableNameCounts(screenCounts);

    const tabs1: TabDescription[] = [];
    for (let tab of getAppTabs(appDescription)) {
        let screenName = getScreenProperty(tab.screenName);
        if (screenName === undefined) {
            log("No screen name for tab");
            continue;
        }

        const reassignedName = screenNameReassignments.get(screenName);
        if (reassignedName !== undefined) {
            tab = { ...tab, screenName: makeScreenProperty(reassignedName) };
            screenName = reassignedName;
        }

        const screen: ScreenDescription | undefined = screenDescriptions[screenName];
        if (screen === undefined) {
            log("Screen not found", screenName);
            continue;
        }

        const tableName = getClassOrArrayScreenTableName(screenName);

        // FIXME: Unify the special screen name check with `isTabSpecial`.
        const isFree = isFreeScreen(screenName, screen);
        if (
            tab.propertyName === undefined &&
            screenName !== chatScreenName &&
            screenName !== shoppingCartScreenName &&
            !isFree
        ) {
            if (tab.title === "Chat") {
                log("Chat screen misconfigured");
                tabs1.push({ ...tab, screenName: chatScreenName });
            } else if (tableName !== undefined) {
                if (screen.kind !== ScreenDescriptionKind.Array && screen.kind !== ScreenDescriptionKind.Class) {
                    log("Screen named class or array screen is not actually one");

                    tabs1.push(tab);
                } else if (tableNameCounts.get(tableName) === 0) {
                    log("No property on class/array screen - setting the property");

                    tabs1.push({ ...tab, propertyName: tableName });
                    tableNameCounts.update(tableName, i => i + 1);
                } else {
                    log("No property on class/array screen - copying to a free screen");

                    const copyName = makeFreeCopy(screenName, screen);
                    tabs1.push({ ...tab, screenName: makeScreenProperty(copyName), propertyName: undefined });
                }
            } else {
                log("Unknown screen without property", screenName);
                tabs1.push(tab);
            }
            continue;
        } else if (tab.propertyName !== undefined && isFree) {
            log("Free screen has propertyName set in tab");
            tabs1.push({ ...tab, propertyName: undefined });
            continue;
        }

        if (tableName !== undefined && tableNameCounts.get(tableName) > 1) {
            log("Duplicate screen for table", screenName, tableName);
            if (tab.propertyName === undefined) {
                log("No property on duplicate screen");
                tabs1.push(tab);
                continue;
            }

            const table = findTable(schema, makeTableName(tab.propertyName));
            if (table === undefined) {
                log("Table for property not found", tab.propertyName);
                tabs1.push(tab);
                continue;
            }

            let newScreenName: string;
            if (getClassScreenTableName(screenName) !== undefined) {
                newScreenName = classScreenName(getTableName(table));
            } else if (getArrayScreenTableName(screenName) !== undefined) {
                newScreenName = arrayScreenName(makeTableRef(table));
            } else {
                log("Neither array nor class screen");
                tabs1.push(tab);
                continue;
            }

            if (appDescription.screenDescriptions[newScreenName] === undefined) {
                log("Correct screen does not exist", newScreenName);
                tabs1.push(tab);
                continue;
            }

            log("rewriting tab", screenName, newScreenName);

            tabs1.push({ ...tab, screenName: makeScreenProperty(newScreenName) });
            continue;
        }

        tabs1.push(tab);
    }

    const tabs2: TabDescription[] = [];
    for (const tab of tabs1) {
        const screenName = defined(getScreenProperty(tab.screenName));
        const screen = defined(screenDescriptions[screenName]);

        if (screen.kind === ScreenDescriptionKind.Array || screen.kind === ScreenDescriptionKind.Class) {
            if (!isFreeScreen(screenName, screen)) {
                log("non-free top-level screen");
                const copyName = makeFreeCopy(screenName, screen);
                tabs2.push({ ...tab, propertyName: undefined, screenName: copyName });
                continue;
            }
        }

        tabs2.push(tab);
    }

    const tabs3 = addOrRemoveSpecialTabsIfNecessary(tabs2, isPage);

    for (const [name, screen] of toPairs(screenDescriptions)) {
        if (screen.kind === ScreenDescriptionKind.Array || screen.kind === ScreenDescriptionKind.Class) {
            if (isFreeScreenName(name)) continue;

            let fixed = { ...screen, filter: undefined, transforms: undefined };
            if (fixed.kind === ScreenDescriptionKind.Array) {
                fixed = { ...fixed, reverse: undefined };
            }

            screenDescriptions[name] = fixed;
        }
    }

    // Return whether the action was handled
    function fixLinkToScreen(
        action: ActionDescription,
        fromScreenName: string,
        screens: Writable<Record<string, Writable<ScreenDescription>>>
    ): boolean {
        if (action.kind !== ActionKind.PushFreeScreen) return false;

        const pushAction = action as PushFreeScreenActionDescription;
        const pushedScreenName = pushAction.screenName;
        if (pushedScreenName !== fromScreenName) {
            const screen = screens[pushedScreenName];
            if (screen?.kind === ScreenDescriptionKind.Class || screen?.kind === ScreenDescriptionKind.Array) {
                const fetchesData = doesLinkToScreenFetchData(pushAction.sourceColumn);
                if (truthify(screen.fetchesData) !== fetchesData) {
                    screen.fetchesData = fetchesData;
                }
                return true;
            }
        }

        // We can't fix this action, so we replace it with a failure toast.
        // Removing the action is actually quite involved, since we have to
        // change the array the action was in, and that can come from a variety
        // of different sources, so we do this right now.
        Object.assign(action, makeShowToastAction(undefined, ToastIcon.Failure));

        return true;
    }

    let fixedAppDescription = { ...appDescription, screenDescriptions, tabs: tabs3 };
    fixedAppDescription = produce(fixedAppDescription, draftApp => {
        forEachActionInApp(draftApp, (action, screenName) => {
            // FIXME: Do this via `fixActionDescrioption`.
            if (fixLinkToScreen(action, screenName, draftApp.screenDescriptions)) return;

            const handler = handlerForActionKind(action.kind);
            // We don't delete it, because we might mess up an app that
            // was made a future build.
            if (handler === undefined) return;
            handler.fixActionDescription(action);
        });
        return draftApp;
    });

    // This is a bad hack for a fundamental issue we currently have: The
    // frontend is responsible for updating the builder app, and the backend
    // is responsible for updating the schema.  Adding a native table requires
    // both, where the frontend makes a call to the backend, the backend adds
    // the native table to the schema, returns the native table ID to the
    // frontend, then the frontend adds it in the builder app.  If the
    // frontend fails to do that, however, we end up with missing source
    // metadata, which trips up `getSourceMetadataForTable`.  This just adds
    // that source metadata if it's missing, but it depends on the native
    // table name to correctly identify its ID, which it might not always:
    // when we copy an app with a native table, we copy the native table, so
    // it gets a new ID, but we keep the original name.  This is not great.
    const existingMetadata = getSourceMetadata(fixedAppDescription);
    const newMetadata: SourceMetadata[] = mapFilterUndefined(schema.tables, t => {
        const tableName = getTableName(t);
        const nativeTableID = getNativeTableIDFromTableName(tableName);
        if (nativeTableID === undefined) return undefined;
        try {
            getSourceMetadataForTable(existingMetadata, tableName);
            return undefined;
        } catch {
            log("Missing metadata for native table", nativeTableID);
            return { type: "Native table", id: nativeTableID, tableName };
        }
    });
    // The above handles the case where we added a Glide Table, but failed to save
    // the builder app just afterwards. This still allows for another sort of drift:
    // the Glide Table isn't present in the schema, but is present in the sourceMetadata.
    // One of the symptoms of this is that you permanently cannot re-add a Glide Table to
    // an app once you unlink it if your network fails in the middle of the process.
    // So to correct for that we also clear out the Glide Tables that aren't present
    // in the schema.
    const replaceMetadata = [...existingMetadata, ...newMetadata];
    const replaceMetadataDeletes = new Set<string>();
    for (const source of newMetadata) {
        if (source.type !== "Native table") continue;

        // FIXME: We shouldn't be hard-coding this prefix here
        const schemaName = makeTableName(`native-table-${source.id}`);
        if (schema.tables.find(t => areTableNamesEqual(getTableName(t), schemaName)) !== undefined) continue;

        replaceMetadataDeletes.add(source.id);
    }

    if (newMetadata.length > 0) {
        fixedAppDescription = {
            ...fixedAppDescription,
            sourceMetadataArray: replaceMetadata.filter(m => !replaceMetadataDeletes.has(m.id)),
        };
    }

    // We don't check strict after fixing, because it also checks for converting
    // features and email filters, which we might not have fixed.
    const issues = checkAppDescriptionConsistency(fixedAppDescription, undefined, true);
    if (issues !== undefined) {
        log("App is still inconsistent", issues);
    }

    return fixedAppDescription;
}

export function fixSchema(schema: TypeSchema): TypeSchema {
    return makeTypeSchema(
        schema.tables.map(t => {
            const columns = t.columns.map(c => {
                if (c.name !== undefined) return c;
                let name: string;
                if (c.displayName === defaultRowIDColumnName) {
                    name = "$rowID";
                } else {
                    name = c.displayName ?? makeRowID();
                }
                return { ...c, name };
            });
            let { rowIDColumn } = t;
            if (getNativeTableIDFromTableName(getTableName(t)) !== undefined) {
                rowIDColumn = "$rowID";
            }
            return { ...t, columns, rowIDColumn };
        })
    );
}
